import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    LotLabelEntryPoint,
    LotLabelLotType,
} from '@/lib/lot-label-print-tracking'
import { packagingLogApi } from '@/lib/api/packaging-log-api'
import { printLabelApi } from '@/lib/api/print-label-api'
import { formatApiError } from '@/lib/format-api-error'
import { captureLotLabelPrint } from '@/lib/lot-label-print-tracking'
import { m } from '@/lib/paraglide/messages'
import { printReceiptHtml } from '@/lib/print-receipt'
import { useCanUseLocalPrint } from '@/hooks/use-can-use-local-print'

/** 5 loại lô có 2 luồng in:
 *  - Local desktop (mouse + hover): fetch HTML tem A7 rồi in bằng
 *    `printReceiptHtml` (iframe ẩn + window.print onload). Không dùng queue.
 *  - Tablet / mobile (pointer coarse hoặc không hover): giữ luồng cũ — enqueue
 *    job vào print queue để Windows agent tại kho in ra máy.
 *  `queueOnly` bỏ hẳn nhánh local, `localOnly` bỏ hẳn nhánh queue — mọi thiết
 *  bị cùng một luồng. Hook nhận một mảng id để "In tất cả" ra đúng một thông
 *  báo (queue) hoặc một hộp thoại in (local). */

type EnqueueFn = (id: string) => Promise<unknown>
/** Nhánh local nhận cả danh sách id; loại lô chỉ in từng tem thì lấy id đầu. */
type FetchHtmlFn = (ids: readonly string[]) => Promise<string>

interface LotLabelPrintOptions {
    queueOnly?: boolean
    localOnly?: boolean
    entryPoint?: LotLabelEntryPoint
}

const firstId =
    (fetchOne: (id: string) => Promise<string>): FetchHtmlFn =>
    (ids) =>
        fetchOne(ids[0])

function createLotLabelPrintHook(
    lotType: LotLabelLotType,
    enqueueFn: EnqueueFn,
    fetchHtmlFn: FetchHtmlFn | null,
    {
        queueOnly = false,
        localOnly = false,
        entryPoint,
    }: LotLabelPrintOptions = {},
) {
    return function usePrintLotLabel() {
        const canUseLocalPrint = useCanUseLocalPrint()
        const localFetch =
            localOnly || (canUseLocalPrint && !queueOnly) ? fetchHtmlFn : null
        const mode = localFetch ? 'local' : 'queue'
        const track = { lotType, mode, entryPoint } as const
        return useMutation({
            mutationFn: async (ids: string | readonly string[]) => {
                const list = typeof ids === 'string' ? [ids] : ids
                captureLotLabelPrint({ ...track, stage: 'clicked' })
                if (localFetch) {
                    const html = await localFetch(list)
                    if (!printReceiptHtml(html)) {
                        captureLotLabelPrint({
                            ...track,
                            stage: 'failed',
                            failure: 'local_open_failed',
                        })
                        throw new Error(m.lot_label_local_print_open_failed())
                    }
                    return { mode: 'local' as const, count: 1 }
                }
                // Tuần tự để job vào hàng đợi đúng thứ tự dòng trên sổ.
                for (const id of list) await enqueueFn(id)
                return { mode: 'queue' as const, count: list.length }
            },
            onSuccess: (result) => {
                captureLotLabelPrint({ ...track, stage: 'succeeded' })
                if (result.mode === 'local') {
                    // Không toast: dialog print của browser đã đủ tín hiệu.
                    return
                }
                toast.success(
                    result.count > 1
                        ? m.lot_label_enqueue_success_many({
                              count: result.count,
                          })
                        : m.lot_label_enqueue_success(),
                    { description: m.lot_label_enqueue_success_hint() },
                )
            },
            onError: (err) => {
                // `local_open_failed` đã phát ngay tại chỗ hỏng trong
                // mutationFn; ở đây chỉ còn lỗi gọi API tem.
                if (!(err instanceof Error) || !isLocalOpenFailure(err)) {
                    captureLotLabelPrint({
                        ...track,
                        stage: 'failed',
                        failure: 'request_failed',
                    })
                }
                const p = formatApiError(err, m.lot_label_enqueue_failed())
                toast.error(p.title, { description: p.description })
            },
        })
    }
}

/** Lỗi do `printReceiptHtml` trả false — đã đếm trong mutationFn, không đếm lại. */
function isLocalOpenFailure(err: Error) {
    return err.message === m.lot_label_local_print_open_failed()
}

export const usePrintSortingLotLabel = createLotLabelPrintHook(
    'sorting',
    printLabelApi.enqueueSortingLot,
    firstId(printLabelApi.getHtmlSortingLot),
)
export const usePrintScentingLotLabel = createLotLabelPrintHook(
    'scenting',
    printLabelApi.enqueueScentingLot,
    firstId(printLabelApi.getHtmlScentingLot),
)
export const usePrintPackagingLotLabel = createLotLabelPrintHook(
    'packaging',
    printLabelApi.enqueuePackagingLot,
    firstId(printLabelApi.getHtmlPackagingLot),
)
export const usePrintProductLotLabel = createLotLabelPrintHook(
    'product',
    printLabelApi.enqueueProductLot,
    firstId(printLabelApi.getHtmlProductLot),
)

/** M-87: tem lô của một (hoặc nhiều) dòng sản phẩm trên sổ 12,5g.
 *  M-134 TẠM THỜI: HTX chưa có máy in nhiệt (hàng đợi không ai in) nên mọi
 *  thiết bị in bằng máy in của trình duyệt. Có máy in nhiệt thì đổi lại
 *  `{ queueOnly: true, ... }` để đi hàng đợi như M-87. */
export const usePrintPackagingLogOutputLabel = createLotLabelPrintHook(
    'product',
    packagingLogApi.printOutputLabel,
    printLabelApi.getHtmlPackagingLogOutputs,
    { localOnly: true, entryPoint: 'packaging_log_12g5' },
)
